import axios from 'axios';
import { DEFAULT_LANG, isLanguageCode } from '../i18n/languages';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  // The JWT lives in an httpOnly cookie — the browser attaches it
  // automatically. `withCredentials` is required to send it cross-origin.
  withCredentials: true,
});

// Language prefix of the current route (URL is the i18n source of
// truth). Falls back to the default language.
function currentLang(): string {
  const seg = window.location.pathname.split('/')[1];
  return isLanguageCode(seg) ? seg : DEFAULT_LANG;
}

// Any 401 means the auth cookie is missing, invalid, or expired. Send
// the user to login with a hard redirect — a full reload resets all
// in-memory React/auth state. Guarded against redirect loops when the
// 401 originates from the auth flow itself.
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      // A 401 from /auth/me just means "not logged in" — that's the
      // expected answer for an anonymous visitor, not a session drop.
      const url = error.config?.url ?? '';
      const isSessionCheck = url.endsWith('/auth/me');
      const onLoginPage = window.location.pathname.endsWith('/login');
      if (!isSessionCheck && !onLoginPage) {
        window.location.assign(`/${currentLang()}/login`);
      }
    }
    return Promise.reject(error);
  },
);

export const api = {
  post: async (endpoint: string, data: any) => {
    try {
      const response = await apiClient.post(endpoint, data);
      return response.data;
    } catch (error: any) {
      return error.response?.data || { error: 'An unexpected error occurred' };
    }
  },
  
  get: async (endpoint: string) => {
    try {
      const response = await apiClient.get(endpoint);
      return response.data;
    } catch (error: any) {
      return error.response?.data || { error: 'An unexpected error occurred' };
    }
  },

  patch: async (endpoint: string, data: unknown) => {
    try {
      const response = await apiClient.patch(endpoint, data);
      return response.data;
    } catch (error) {
      return (
        (axios.isAxiosError(error) && error.response?.data) || {
          error: 'An unexpected error occurred',
        }
      );
    }
  },

  delete: async (endpoint: string) => {
    try {
      const response = await apiClient.delete(endpoint);
      return response.data;
    } catch (error) {
      return (
        (axios.isAxiosError(error) && error.response?.data) || {
          error: 'An unexpected error occurred',
        }
      );
    }
  },
};
